import React, { useMemo } from 'react';
import { Card, CardBody, Row, Col, Badge } from 'reactstrap'; // Assuming Progress is not used from reactstrap here
import { Icon } from '../../../components/Component'; // Assuming path is correct

const LeadFunnel = ({
  data: propData, // Renamed to propData to avoid conflict with internal data variable
  title = "Lead Conversion Funnel",
  showPercentages = true,
  isHotLeadData = false
}) => {
  const defaultData = useMemo(() => ({
    leadAssigned: 0,
    followUp: 0,
    prospect: 0,
    listingOrDeal: 0,
    won: 0,
    // For hot lead specific summary stats if available directly
    hotLeadConversionRate: undefined,
    hotLeadDropOffRate: undefined,
    // ... other potential hotLead specific keys from data structure
    hotLeadsAssigned: 0,
    hotLeadsFollowedUp: 0,
    hotLeadsConverted: 0,
    hotLeadsListingDeals: 0,
    hotLeadsWon: 0,
  }), []);

  const data = useMemo(() => ({ ...defaultData, ...propData }), [defaultData, propData]);

  const normalizedData = useMemo(() => {
    if (isHotLeadData) {
      return {
        leadAssigned: data.hotLeadsAssigned || 0,
        followUp: data.hotLeadsFollowedUp || 0,
        prospect: data.hotLeadsConverted || 0,
        listingOrDeal: data.hotLeadsListingDeals || 0,
        won: data.hotLeadsWon || 0,
      };
    }
    return { // Ensure all keys exist even if not hotLeadData
      leadAssigned: data.leadAssigned || 0,
      followUp: data.followUp || 0,
      prospect: data.prospect || 0,
      listingOrDeal: data.listingOrDeal || 0,
      won: data.won || 0,
    };
  }, [data, isHotLeadData]);

  const initialValue = normalizedData.leadAssigned || 1; // Prevent division by zero for overall percentages

  const stages = useMemo(() => [
    { key: 'leadAssigned', label: 'Leads Assigned', color: '#4361ee', icon: 'user-add' },
    { key: 'followUp', label: 'Followed Up', color: '#4cc9f0', icon: 'call' },
    { key: 'prospect', label: 'Prospects', color: '#7209b7', icon: 'users' },
    { key: 'listingOrDeal', label: 'Listings/Deals', color: '#f72585', icon: 'file-text' },
    { key: 'won', label: 'Won', color: '#2dce89', icon: 'check-circle' }
  ], []);

  const percentages = useMemo(() => {
    let calculatedPercentages = {};
    stages.forEach(stage => {
      calculatedPercentages[stage.key] = Math.round((normalizedData[stage.key] / initialValue) * 100);
    });
    return calculatedPercentages;
  }, [normalizedData, initialValue, stages]);

  const numTransitions = stages.length > 1 ? stages.length - 1 : 1; // For drop-off calculation

  return (
    <Card className="shadow-sm border-0 h-100" style={{ transition: 'none' }}>
      <CardBody className="p-3 p-md-4 d-flex flex-column">
        <h5 className="card-title fw-semibold mb-3">{title}</h5>

        <div className="flex-grow-1"> {/* Main content area for funnel steps */}
          {stages.map((stage, index) => {
            const currentValue = normalizedData[stage.key] || 0;
            const prevStageKey = index > 0 ? stages[index - 1].key : null;
            const prevValue = prevStageKey ? (normalizedData[prevStageKey] || 0) : currentValue; // if first stage, prevValue is currentValue for 100%

            // Step percentage: conversion from previous step. For first step, it's 100% of itself.
            const stepPercentage = index === 0 || prevValue === 0 ? (currentValue > 0 ? 100 : 0) : Math.round((currentValue / prevValue) * 100);

            return (
              <div key={stage.key} className="mb-2"> {/* Was: funnel-step */}
                <div className="d-flex align-items-center mb-1">
                  <div
                    className="rounded-circle text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      backgroundColor: stage.color,
                      width: '32px',
                      height: '32px',
                    }}
                  >
                    <Icon name={stage.icon} style={{ fontSize: '1rem' }} />
                  </div>
                  <div className="flex-grow-1"> {/* Was: funnel-step-info */}
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-medium small">{stage.label}</h6>
                      <div>
                        <Badge color="light" text="dark" pill className="px-2 py-1 border shadow-sm">
                          <span className="fw-bold">{currentValue}</span>
                          {showPercentages && index > 0 && prevValue > 0 && ( // Show step % only if not first and prevValue > 0
                            <span className="ms-1 text-muted" style={{ fontSize: '0.7rem' }}>
                              ({stepPercentage}%)
                            </span>
                          )}
                        </Badge>
                      </div>
                    </div>
                    <div className="progress mt-1" style={{ height: '6px', backgroundColor: 'var(--bs-gray-200)' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${percentages[stage.key]}%`,
                          backgroundColor: stage.color
                        }}
                        aria-valuenow={percentages[stage.key]}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                </div>
                {index < stages.length - 1 && (
                  <div className="ms-2 ps-2" style={{
                    height: '12px',
                    borderLeft: `2px dashed var(--bs-gray-300)`, // Using Bootstrap variable for connector color
                    marginLeft: '14px', // Aligns with center of 32px icon
                  }}></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="mt-auto pt-3 border-top"> {/* mt-auto pushes summary to bottom if card has fixed height or d-flex flex-column */}
          <Row className="g-2 text-center mb-2">
            <Col>
              <div className="small text-muted">Overall Conversion</div>
              <div className="h5 fw-bold mb-0">
                {isHotLeadData && data.hotLeadConversionRate !== undefined
                  ? `${Math.round(data.hotLeadConversionRate)}%`
                  : `${initialValue > 0 ? Math.round((normalizedData.won / initialValue) * 100) : 0}%`}
              </div>
            </Col>
            <Col>
              <div className="small text-muted">Avg. Step Drop-off</div>
              <div className="h5 fw-bold mb-0">
                {isHotLeadData && data.hotLeadDropOffRate !== undefined
                  ? `${Math.round(data.hotLeadDropOffRate)}%`
                  : `${initialValue > 0 && normalizedData.won > 0 && stages.length > 1
                    ? Math.round((1 - Math.pow(normalizedData.won / initialValue, 1 / numTransitions)) * 100)
                    : 0}%`}
              </div>
            </Col>
          </Row>
          <Row className="g-2">
            <Col xs="4">
              <div className="bg-light rounded p-2 text-center">
                <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Total Leads</div>
                <div className="h6 fw-bold mb-0">{normalizedData.leadAssigned}</div>
              </div>
            </Col>
            <Col xs="4">
              <div className="bg-light rounded p-2 text-center">
                <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Prospects</div>
                <div className="h6 fw-bold mb-0">{normalizedData.prospect}</div>
              </div>
            </Col>
            <Col xs="4">
              <div className="bg-light rounded p-2 text-center">
                <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Deals Won</div>
                <div className="h6 fw-bold mb-0">{normalizedData.won}</div>
              </div>
            </Col>
          </Row>
        </div>
      </CardBody>
    </Card>
  );
};

export default LeadFunnel;
